import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { toast } from 'sonner';
import { z } from 'zod';
import Honeybadger from '@honeybadger-io/js';
import { zodResolver } from '@hookform/resolvers/zod';

import { deserializer } from '../../../../shared/utils/deserializer';
import { SUCCESS_MESSAGES } from '../constants';
import {
  createTask as apiCreateTask,
  updateTask as apiUpdateTask,
} from '../services/taskService';
import useBoard from './useBoard';
import useDialogs from './useDialogs';

const formSchema = z.object({
  assigneeId: z.string().optional(),
  caseFileId: z.string().optional(),
  name: z.string().min(1, 'Name is required.'),
  notes: z.string().optional(),
  priority: z.string().nullable().optional(),
  tagIds: z.array(z.string()).optional(),
  taskStatusId: z.string().min(1, 'Status is required.'),
});

const useTaskForm = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { activeTask, handleCloseTaskFormDialog: handleClose } = useDialogs();
  const { addTask, updateTask: onUpdate } = useBoard();

  const defaultValues = {
    assigneeId: activeTask?.assignee?.id ?? '',
    caseFileId: activeTask?.caseFile?.id ?? '',
    name: activeTask?.name ?? '',
    notes: activeTask?.notes ?? '',
    priority: activeTask?.priority ?? '',
    tagIds: activeTask?.tags?.map((tag) => tag.id) ?? [],
    taskStatusId: activeTask?.taskStatus?.id ?? '',
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // For repopulating :defaultValues when :activeTask changes.
  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask]);

  const createTask = async (taskData) => {
    const serializedTask = await apiCreateTask(taskData);
    return deserializer.deserialize(serializedTask);
  };

  const updateTask = async (taskData, taskId) => {
    const serializedTask = await apiUpdateTask({ taskData, taskId });
    return deserializer.deserialize(serializedTask);
  };

  const handleSuccess = (newTask) => {
    if (activeTask) {
      onUpdate(newTask);
      toast.success(SUCCESS_MESSAGES.task.update);
    } else {
      addTask(newTask);
      toast.success(SUCCESS_MESSAGES.task.create);
    }

    handleClose();
    form.reset();
  };

  const handleSubmit = async (formData) => {
    try {
      setErrorMessage('');
      setIsSubmitting(true);

      const taskData = {
        ...formData,
        assigneeId: formData.assigneeId || null,
        caseFileId: formData.caseFileId || null,
        priority: formData.priority || null,
        tagIds: formData.tagIds ?? [],
      };

      const newTask = activeTask
        ? await updateTask(taskData, activeTask.id)
        : await createTask(taskData);

      handleSuccess(newTask);
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      handleClose();
      setErrorMessage('');
      form.reset();
    }
  };

  return {
    errorMessage,
    form,
    handleOpenChange,
    handleSubmit,
    isSubmitting,
  };
};

export default useTaskForm;
